import React, { useEffect, useRef } from 'react';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import './GlobalTourMap.css';

const GlobalTourMap = () => {
  const mapRef = useRef(null);

  useEffect(() => {
    if (mapRef.current) {
      const map = new jsVectorMap({
        selector: '#global-tour-map',
        map: 'world',
        backgroundColor: '#f8f9fa',
        regionStyle: {
          initial: {
            fill: '#e0e0e0',
            stroke: '#ffffff',
            strokeWidth: 1,
          },
          hover: {
            fill: '#c0c0c0',
            fillOpacity: 1,
            cursor: 'pointer',
          },
        },
        markers: [
          { name: 'New Delhi (Home Base)', coords: [28.6139, 77.2090], style: { fill: '#ff6b35', r: 8 } },
          { name: 'Dubai', coords: [25.2048, 55.2708], style: { fill: '#dc2626' } },
          { name: 'Riyadh', coords: [24.7136, 46.6753], style: { fill: '#dc2626' } },
          { name: 'Doha', coords: [25.2854, 51.5310], style: { fill: '#dc2626' } },
          { name: 'Singapore', coords: [1.3521, 103.8198], style: { fill: '#c173dd' } },
          { name: 'Kuala Lumpur', coords: [3.1390, 101.6869], style: { fill: '#c173dd' } },
          { name: 'Bangkok', coords: [13.7563, 100.5018], style: { fill: '#c173dd' } },
          { name: 'Ho Chi Minh City', coords: [10.8231, 106.6297], style: { fill: '#c173dd' } },
          { name: 'Tokyo', coords: [35.6762, 139.6503], style: { fill: '#f57919' } },
          { name: 'Sydney', coords: [-33.8688, 151.2093], style: { fill: '#f57919' } },
          { name: 'Melbourne', coords: [-37.8136, 144.9631], style: { fill: '#f57919' } },
          { name: 'London', coords: [51.5074, -0.1278], style: { fill: '#0fbde7' } },
          { name: 'Paris', coords: [48.8566, 2.3522], style: { fill: '#0fbde7' } },
          { name: 'Berlin', coords: [52.5200, 13.4050], style: { fill: '#0fbde7' } },
          { name: 'Milan', coords: [45.4642, 9.1900], style: { fill: '#0fbde7' } },
          { name: 'Madrid', coords: [40.4168, -3.7038], style: { fill: '#0fbde7' } },
          { name: 'Amsterdam', coords: [52.3676, 4.9041], style: { fill: '#0fbde7' } },
          { name: 'Johannesburg', coords: [-26.2041, 28.0473], style: { fill: '#253cf6' } },
          { name: 'Nairobi', coords: [-1.2864, 36.8172], style: { fill: '#253cf6' } },
          { name: 'Accra', coords: [5.6037, -0.1870], style: { fill: '#253cf6' } },
          { name: 'New York', coords: [40.7128, -74.0060], style: { fill: '#32cd32' } },
          { name: 'Los Angeles', coords: [34.0522, -118.2437], style: { fill: '#32cd32' } },
          { name: 'Toronto', coords: [43.6532, -79.3832], style: { fill: '#32cd32' } },
          { name: 'Vancouver', coords: [49.2827, -123.1207], style: { fill: '#32cd32' } },
        ],
        markerStyle: {
          initial: {
            stroke: '#ffffff',
            strokeWidth: 2,
          },
          hover: {
            stroke: '#ffffff',
            strokeWidth: 3,
            cursor: 'pointer',
          },
        },
        markerLabelStyle: {
          initial: {
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 'bold',
            fill: '#333',
          },
        },
        regionLabelStyle: {
          initial: {
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fill: '#333',
          },
        },
        zoomButtons: true,
        zoomOnScroll: true,
        zoomMax: 10,
        zoomMin: 1,
        onRegionTooltipShow(event, tooltip, code) {
          // Get the country name from the map
          const region = event.target.correspondingElement.__data__.properties.name;
          
          const countryNames = {
            'AE': '🇦🇪 UAE - Middle East Launch',
            'SA': '🇸🇦 Saudi Arabia - Middle East Launch',
            'QA': '🇶🇦 Qatar - Middle East Launch',
            'SG': '🇸🇬 Singapore - Southeast Asia Expansion',
            'MY': '🇲🇾 Malaysia - Southeast Asia Expansion',
            'TH': '🇹🇭 Thailand - Southeast Asia Expansion',
            'VN': '🇻🇳 Vietnam - Southeast Asia Expansion',
            'JP': '🇯🇵 Japan - East Asia & Oceania Connect',
            'AU': '🇦🇺 Australia - East Asia & Oceania Connect',
            'GB': '🇬🇧 United Kingdom - Europe Leadership Tour',
            'FR': '🇫🇷 France - Europe Leadership Tour',
            'DE': '🇩🇪 Germany - Europe Leadership Tour',
            'IT': '🇮🇹 Italy - Europe Leadership Tour',
            'ES': '🇪🇸 Spain - Europe Leadership Tour',
            'NL': '🇳🇱 Netherlands - Europe Leadership Tour',
            'ZA': '🇿🇦 South Africa - Africa Rising',
            'KE': '🇰🇪 Kenya - Africa Rising',
            'GH': '🇬🇭 Ghana - Africa Rising',
            'US': '🇺🇸 USA - Americas Grand Finale',
            'CA': '🇨🇦 Canada - Americas Grand Finale',
            'IN': '🇮🇳 India - Home Base (Starting Point)',
          };

          // Show custom text for tour countries, otherwise show the country name
          const displayText = countryNames[code] || region;
          tooltip.text(displayText, true);
        },
        onMarkerTooltipShow(event, tooltip, index) {
          const markers = [
            '🇮🇳 New Delhi, India (Home Base)',
            '🇦🇪 Dubai, UAE',
            '🇸🇦 Riyadh, Saudi Arabia',
            '🇶🇦 Doha, Qatar',
            '🇸🇬 Singapore',
            '🇲🇾 Kuala Lumpur, Malaysia',
            '🇹🇭 Bangkok, Thailand',
            '🇻🇳 Ho Chi Minh City, Vietnam',
            '🇯🇵 Tokyo, Japan',
            '🇦🇺 Sydney, Australia',
            '🇦🇺 Melbourne, Australia',
            '🇬🇧 London, United Kingdom',
            '🇫🇷 Paris, France',
            '🇩🇪 Berlin, Germany',
            '🇮🇹 Milan, Italy',
            '🇪🇸 Madrid, Spain',
            '🇳🇱 Amsterdam, Netherlands',
            '🇿🇦 Johannesburg, South Africa',
            '🇰🇪 Nairobi, Kenya',
            '🇬🇭 Accra, Ghana',
            '🇺🇸 New York, USA',
            '🇺🇸 Los Angeles, USA',
            '🇨🇦 Toronto, Canada',
            '🇨🇦 Vancouver, Canada',
          ];
          if (markers[index]) {
            tooltip.text(markers[index], true);
          }
        },
      });

      // Apply colors to markers after map is rendered
      setTimeout(() => {
        const markers = document.querySelectorAll('#global-tour-map circle[class*="jsvectormap-marker"]');
        const colors = [
          '#ff6b35', '#dc2626', '#dc2626', '#dc2626',
          '#c173dd', '#c173dd', '#c173dd', '#c173dd',
          '#f57919', '#f57919', '#f57919',
          '#0fbde7', '#0fbde7', '#0fbde7', '#0fbde7', '#0fbde7', '#0fbde7',
          '#253cf6', '#253cf6', '#253cf6',
          '#32cd32', '#32cd32', '#32cd32', '#32cd32',
        ];
        const sizes = [8, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6];
        
        markers.forEach((marker, index) => {
          if (colors[index]) {
            marker.setAttribute('fill', colors[index]);
            marker.style.fill = colors[index];
            if (sizes[index]) {
              marker.setAttribute('r', sizes[index]);
            }
          }
        });
      }, 100);

      return () => {
        if (map) {
          map.destroy();
        }
      };
    }
  }, []);

  return (
    <div className="global-tour-map-wrapper">
      <div ref={mapRef} id="global-tour-map" className="jsvectormap-container"></div>
      <div className="map-legend">
        <h4>Tour Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#dc2626' }}></span>
            <span>Trip 1: Middle East</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#c173dd' }}></span>
            <span>Trip 2: Southeast Asia</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#f57919' }}></span>
            <span>Trip 3: East Asia & Oceania</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#0fbde7' }}></span>
            <span>Trip 4: Europe</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#253cf6' }}></span>
            <span>Trip 5: Africa</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#32cd32' }}></span>
            <span>Trip 6: Americas</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#ff6b35' }}></span>
            <span>Trip Starting Point: India</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalTourMap;

